<template>
  <v-container v-if="isAdmin" class="owners">
    <v-btn color="primary" dark elevation="2" fab fixed bottom right :to="{ name: 'Owner', params: { id: 'new' } }">
      <v-icon>mdi-plus</v-icon>
    </v-btn>
    <v-row>
      <v-col cols="12" class="mt-3 mb-3">
        <h2 class="text-h4">Оператори</h2>
      </v-col>
      <v-col v-if="loadError" cols="12">
        <v-alert type="error">
          {{ loadError }}
          <v-btn text small :disabled="isPageLoading" @click="init">Спробувати ще раз</v-btn>
        </v-alert>
      </v-col>
      <v-col cols="12">
        <v-sheet outlined rounded class="owners_table_wrapper">
          <v-data-table
            :headers="tableHeaders"
            :items="owners"
            hide-default-footer
            disable-filtering
            multi-sort
            no-data-text="На жаль, нема жодного оператора."
            :items-per-page="-1">
            <template v-slot:item.name="{ item }">
              {{ item.name }}
              <span v-if="item.note" class="owners_table_note">{{ item.note }}</span>
            </template>
            <template v-slot:item.phone="{ item }">
              <a :href="`tel:${item.phone}`">{{ item.phone }}</a>
            </template>
            <template v-slot:item.email="{ item }">
              <a :href="`mailto:${item.email}`">{{ item.email }}</a>
            </template>
            <template v-slot:item.pin="{ item }">
              <span class="owners_pin">{{ item.pin || '—' }}</span>
              <v-btn small icon title="Змінити PIN-код" :disabled="isSavingPin" @click="openPinDialog(item)">
                <v-icon small>mdi-key-variant</v-icon>
              </v-btn>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn small icon class="mr-2" :to="{ name: 'Owner', params: { id: item.id } }">
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
              <v-btn small icon :disabled="isOwnerBeingDeleted" @click.stop="deleteOwnerConfirm(item)">
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-sheet>
      </v-col>
    </v-row>
    <AppPageLoader :visible="isPageLoading" />
    <v-dialog v-model="isConfirmDeleteOwnerVisible" :persistent="isOwnerBeingDeleted" max-width="500px">
      <v-card :loading="isOwnerBeingDeleted">
        <v-card-title class="pt-5 pb-5">Видалити цього оператора?</v-card-title>
        <v-card-text v-if="deleteError"><v-alert type="error">{{ deleteError }}</v-alert></v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text :disabled="isOwnerBeingDeleted" @click="closeDeleteOwner">Відмінити</v-btn>
          <v-btn color="red" text :disabled="isOwnerBeingDeleted" :loading="isOwnerBeingDeleted" @click="deleteOwner">Видалити</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="isPinDialogVisible" :persistent="isSavingPin" max-width="500px">
      <v-card :loading="isSavingPin">
        <v-card-title class="pt-5 pb-5">Змінити PIN-код</v-card-title>
        <v-card-text>
          <p v-if="pinOwner">{{ pinOwner.name }}</p>
          <v-alert v-if="pinError" type="error" dense>{{ pinError }}</v-alert>
          <v-form @submit.prevent="savePin">
            <v-text-field
              v-model="pinValue"
              label="Новий PIN-код"
              type="password"
              inputmode="numeric"
              maxlength="6"
              pattern="[0-9]{6}"
              autocomplete="off"
              outlined
              hide-details="auto"
              :disabled="isSavingPin"
              :error-messages="pinErrors"
              @input="clearPinErrors"
              @blur="$v.pinValue.$touch()" />
          </v-form>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="grey" :disabled="isSavingPin" @click="isPinDialogVisible = false">Відмінити</v-btn>
          <v-btn text color="primary" :disabled="isSavingPin" :loading="isSavingPin" @click="savePin">Зберегти</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-snackbar v-model="snack" :timeout="5000" color="success">PIN-код змінено.</v-snackbar>
  </v-container>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import { GET_OWNERS, DELETE_OWNER, UPDATE_OWNER_PIN } from '@/store/owners/types';

export default {
  data: () => ({
    ownerToDelete: null,
    isOwnerBeingDeleted: false,
    isConfirmDeleteOwnerVisible: false,
    isPageLoading: false,
    loadError: '',
    deleteError: '',
    isPinDialogVisible: false,
    isSavingPin: false,
    pinOwner: null,
    pinValue: '',
    pinError: '',
    pinServerErrors: [],
    snack: false,
    tableHeaders: [
      { text: 'Назва', value: 'name' },
      { text: 'Телефон', value: 'phone' },
      { text: 'Email', value: 'email' },
      { text: 'PIN-код', value: 'pin', sortable: false, width: 145 },
      { text: 'Дія', value: 'actions', sortable: false, width: 110 },
    ],
  }),
  computed: {
    isAdmin() { return this.$store.getters.isAdmin; },
    authIdentity() { return this.$store.getters.authIdentity; },
    owners() { return this.isAdmin ? this.$store.getters.owners : []; },
    pinErrors() {
      const errors = [...this.pinServerErrors];
      if (!this.$v.pinValue.$dirty) return errors;
      if (!this.$v.pinValue.required) errors.push('Вкажіть PIN-код.');
      else if (!this.$v.pinValue.sixDigits) errors.push('PIN-код має містити рівно 6 цифр.');
      return errors;
    },
  },
  watch: {
    authIdentity() {
      this.isPinDialogVisible = false;
      this.clearPin();
      this.ownerToDelete = null;
      this.isConfirmDeleteOwnerVisible = false;
    },
    isPinDialogVisible(visible) {
      if (!visible) this.clearPin();
    },
  },
  methods: {
    apiError(error, fallback) {
      const data = error && error.response && error.response.data;
      return data && typeof data.message === 'string' ? data.message : fallback;
    },
    async init() {
      if (!this.isAdmin || this.isPageLoading) return;
      this.isPageLoading = true;
      this.loadError = '';
      try {
        await this.getOwners();
      } catch (error) {
        this.loadError = this.apiError(error, 'Не вдалося завантажити операторів.');
      } finally {
        this.isPageLoading = false;
      }
    },
    async getOwners() {
      return this.$store.dispatch(GET_OWNERS);
    },
    async deleteOwner() {
      if (!this.isAdmin || this.isOwnerBeingDeleted || !this.ownerToDelete) return;
      this.isOwnerBeingDeleted = true;
      this.deleteError = '';
      try {
        await this.$store.dispatch(DELETE_OWNER, this.ownerToDelete.id);
        this.closeDeleteOwner();
        await this.init();
      } catch (error) {
        this.deleteError = this.apiError(error, 'Не вдалося видалити оператора.');
      } finally {
        this.isOwnerBeingDeleted = false;
      }
    },
    closeDeleteOwner() {
      this.ownerToDelete = null;
      this.isConfirmDeleteOwnerVisible = false;
    },
    deleteOwnerConfirm(owner) {
      if (!this.isAdmin || this.isOwnerBeingDeleted) return;
      this.ownerToDelete = owner;
      this.deleteError = '';
      this.isConfirmDeleteOwnerVisible = true;
    },
    clearPin() {
      this.pinOwner = null;
      this.pinValue = '';
      this.pinServerErrors = [];
      this.pinError = '';
    },
    clearPinErrors() {
      this.pinServerErrors = [];
      this.pinError = '';
    },
    openPinDialog(owner) {
      if (!this.isAdmin || this.isSavingPin) return;
      this.pinOwner = { id: owner.id, name: owner.name };
      this.pinValue = owner.pin || '';
      this.clearPinErrors();
      this.$v.$reset();
      this.isPinDialogVisible = true;
    },
    async savePin() {
      if (!this.isAdmin || this.isSavingPin || !this.pinOwner) return;
      this.$v.$touch();
      if (this.$v.$invalid) return;
      this.isSavingPin = true;
      this.clearPinErrors();
      try {
        await this.$store.dispatch(UPDATE_OWNER_PIN, { id: this.pinOwner.id, pin: this.pinValue });
        if (!this.isAdmin) return;
        this.isPinDialogVisible = false;
        this.clearPin();
        this.snack = true;
        await this.init();
      } catch (error) {
        const response = error && error.response;
        const errors = response && response.status === 422 && response.data && response.data.errors;
        if (errors && errors.pin) this.pinServerErrors = Array.isArray(errors.pin) ? errors.pin : [errors.pin];
        else this.pinError = this.apiError(error, 'Не вдалося змінити PIN-код.');
      } finally {
        this.isSavingPin = false;
      }
    },
  },
  created() { this.init(); },
  beforeDestroy() { this.clearPin(); },
  validations: {
    pinValue: { required, sixDigits: (value) => /^[0-9]{6}$/.test(value) },
  },
};
</script>

<style lang="scss" scoped>
.owners { max-width: 950px; }
.owners_table_wrapper { margin-bottom: 72px; }
.owners_table_note {
  display: block;
  font-size: 0.85em;
  font-style: italic;
  opacity: 0.5;
}
.owners_pin { font-variant-numeric: tabular-nums; }
</style>
